import { type MouseEvent } from 'react';
import { format } from 'date-fns';
import {
  Calendar,
  ChevronRight,
  Clock,
  CreditCard,
  DoorOpen,
  Repeat,
  Ticket,
  Users,
} from 'lucide-react';
import type { Region } from '@court-sync/shared';
import type { PublicPlayEventSummary } from '@/services/playEvent';
import { Badge, Button, Card } from '@/components/ui';
import { SportIconTile } from '@/components/sport/SportIcons';
import { PublicPlayEventCoverImage } from '@/components/public/play/PublicPlayEventCoverImage';
import {
  formatPlayEventDurationMinutes,
  getPlayEventFromPriceLabel,
} from '@/lib/playEventPublicDisplay';
import { cn } from '@/lib/utils';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_EVENT_SIDE_LABELS,
  PLAY_JOIN_POLICY_LABELS,
  PLAY_PAYMENT_MODE_LABELS,
  getPublicPlayEventStatusBadgeVariant,
  getPublicPlayEventStatusLabel,
} from '@/lib/playEventLabels';

export interface PublicPlayEventCardProps {
  event: PublicPlayEventSummary;
  onOpen: () => void;
  orgRegion?: Region | string | null;
  className?: string;
  featured?: boolean;
  /** When true, uses a horizontal banner layout suited to full-width placement. */
  fullWidth?: boolean;
}

function getCtaLabel(event: PublicPlayEventSummary): string {
  if (event.spotsRemaining === 0) {
    return 'View event';
  }
  if (event.status === 'completed' || event.status === 'cancelled') {
    return 'View details';
  }
  return 'Join event';
}

export function PublicPlayEventCard({
  event,
  onOpen,
  orgRegion,
  className,
  featured = false,
  fullWidth = false,
}: PublicPlayEventCardProps) {
  const start = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
  const end = event.endsAt instanceof Date ? event.endsAt : new Date(event.endsAt);
  const publicStatusLabel = getPublicPlayEventStatusLabel(event.status);
  const publicStatusVariant = getPublicPlayEventStatusBadgeVariant(event.status);
  const durationLabel = formatPlayEventDurationMinutes(start, end);
  const priceLabel = getPlayEventFromPriceLabel(event, orgRegion);
  const PaymentIcon = event.paymentMode === 'online_on_join' ? CreditCard : DoorOpen;
  const isBanner = featured && fullWidth;
  const hasCover = Boolean(event.coverImageUrl?.trim());
  const ctaLabel = getCtaLabel(event);
  const isFull = event.spotsRemaining === 0;

  const handleOpen = (e?: MouseEvent) => {
    e?.stopPropagation();
    onOpen();
  };

  const spotsLabel =
    event.spotsRemaining != null
      ? isFull
        ? 'Full'
        : `${event.spotsRemaining} spot${event.spotsRemaining === 1 ? '' : 's'} left`
      : event.capacity != null
        ? `Up to ${event.capacity} players`
        : null;

  const metaItems = [
    {
      key: 'date',
      icon: Calendar,
      content: <time dateTime={start.toISOString()}>{format(start, 'EEE d MMM yyyy')}</time>,
    },
    {
      key: 'time',
      icon: Clock,
      content: (
        <span>
          {format(start, 'h:mm a')}
          {durationLabel ? ` · ${durationLabel}` : null}
        </span>
      ),
    },
    ...(event.roundCount
      ? [
          {
            key: 'rounds',
            icon: Ticket,
            content: <span>{event.roundCount} rounds</span>,
          },
        ]
      : []),
    {
      key: 'payment',
      icon: PaymentIcon,
      content: <span>{PLAY_PAYMENT_MODE_LABELS[event.paymentMode]}</span>,
    },
    ...(spotsLabel
      ? [
          {
            key: 'spots',
            icon: Users,
            content: (
              <span className={isFull ? 'font-medium text-amber-700 dark:text-amber-300' : undefined}>
                {spotsLabel}
                {event.spotsRemaining != null && event.capacity != null && !isFull
                  ? ` · ${event.capacity} capacity`
                  : null}
              </span>
            ),
          },
        ]
      : []),
  ];

  const actionBar = (
    <div
      className={cn(
        'flex shrink-0 items-center justify-between gap-3 border-t border-border/60 bg-muted/30 px-4 py-3 sm:px-5',
        isBanner && 'sm:px-6'
      )}
    >
      <div className="min-w-0">
        {priceLabel ? (
          <p className="text-base font-semibold text-foreground">{priceLabel}</p>
        ) : (
          <p className="text-sm text-muted-foreground">See details for pricing</p>
        )}
      </div>
      <Button
        size="sm"
        variant={isFull ? 'outline' : 'default'}
        className="shrink-0"
        onClick={handleOpen}
      >
        {ctaLabel}
        <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
      </Button>
    </div>
  );

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => onOpen()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'group h-full cursor-pointer overflow-hidden border-border/80 transition-all hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        featured && 'border-primary/20 bg-gradient-to-br from-card to-primary/5',
        className
      )}
    >
      <div
        className={cn(
          'flex h-full flex-col',
          hasCover && 'sm:flex-row sm:items-stretch'
        )}
      >
        {hasCover ? (
          <div
            className={cn(
              'relative shrink-0 overflow-hidden border-b border-border/60 bg-muted sm:border-b-0 sm:border-r',
              isBanner
                ? 'aspect-[16/10] sm:aspect-auto sm:min-h-[12rem] sm:w-[42%] sm:max-w-md lg:max-w-lg'
                : 'aspect-[16/10] sm:aspect-auto sm:min-h-[11rem] sm:w-[38%] sm:max-w-[14rem] md:max-w-[16rem]'
            )}
          >
            <PublicPlayEventCoverImage event={event} fill className="absolute inset-0" />
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          <div
            className={cn(
              'flex flex-1 flex-col gap-4',
              featured ? 'p-6' : 'p-5',
              isBanner && hasCover && 'sm:py-6'
            )}
          >
            <div className={cn('flex items-start gap-3', isBanner && 'lg:items-center')}>
              {!hasCover ? (
                <div
                  className={cn(
                    'flex shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary',
                    featured ? 'h-14 w-14' : 'h-11 w-11'
                  )}
                  aria-hidden
                >
                  <SportIconTile sportId={event.sportId} className={featured ? 'h-8 w-8' : 'h-6 w-6'} />
                </div>
              ) : null}
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h3
                    className={cn(
                      'font-semibold leading-tight text-foreground group-hover:text-primary',
                      featured ? 'text-xl' : 'text-lg'
                    )}
                  >
                    {event.name}
                  </h3>
                  <Badge variant={publicStatusVariant ?? 'secondary'} className="shrink-0 text-xs">
                    {publicStatusLabel ?? 'Open'}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-xs font-normal">
                    {PLAY_EVENT_FORMAT_LABELS[event.format]}
                  </Badge>
                  <Badge variant="outline" className="text-xs font-normal">
                    {PLAY_EVENT_SIDE_LABELS[event.side]}
                  </Badge>
                  <Badge variant="outline" className="text-xs font-normal">
                    {PLAY_JOIN_POLICY_LABELS[event.joinPolicy]}
                  </Badge>
                  {event.isRecurring ? (
                    <Badge variant="secondary" className="gap-1 text-xs font-normal">
                      <Repeat className="h-3 w-3" aria-hidden />
                      Weekly
                    </Badge>
                  ) : null}
                </div>
                {event.isRecurring && event.recurrenceLabel ? (
                  <p className="text-xs text-muted-foreground">{event.recurrenceLabel}</p>
                ) : null}
              </div>
            </div>

            <div
              className={cn(
                'grid gap-2 text-sm text-muted-foreground',
                isBanner ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'
              )}
            >
              {metaItems.map(({ key, icon: Icon, content }) => (
                <p key={key} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  {content}
                </p>
              ))}
            </div>
          </div>

          {actionBar}
        </div>
      </div>
    </Card>
  );
}
